import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { fetchPractitioners } from '../lib/supabase';
import { images } from '../data/mockData';
import { formatPractitionerName, formatProfileText } from '../lib/profileFormatting';
import { getResponsiveImageUrl } from '../lib/imageOptimization';

const getPhotoUrl = (photo) => {
  if (!photo) return '';
  if (photo.startsWith('wix:image://')) {
    const mediaId = photo.match(/^wix:image:\/\/v1\/([^/]+)/)?.[1];
    return mediaId ? `https://static.wixstatic.com/media/${mediaId}` : '';
  }
  return photo;
};

const getRandomPractitioners = (practitioners) => {
  const shuffled = [...practitioners];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }
  return shuffled.slice(0, 3);
};

const HeroPractitionerCard = ({ practitioner, featured }) => {
  const profileId = practitioner.slug || practitioner.id || practitioner.ID;
  const photoUrl = getResponsiveImageUrl(getPhotoUrl(practitioner.Photo), { width: 760 });
  const practitionerName = formatPractitionerName(practitioner.Name);

  return (
    <Link
      to={`/practitioners/${profileId}`}
      className={`group relative block overflow-hidden rounded-2xl border border-yellow-200/20 bg-purple-950/70 shadow-[0_20px_60px_rgba(5,2,15,0.45)] transition duration-500 hover:-translate-y-2 hover:border-yellow-200/60 hover:shadow-[0_24px_70px_rgba(126,70,230,0.45)] ${
        featured ? 'sm:-translate-y-5' : ''
      }`}
      aria-label={`View ${practitionerName}'s practitioner profile`}
    >
      <div className={`relative overflow-hidden ${featured ? 'h-72 sm:h-[25rem]' : 'h-64 sm:h-[21rem]'}`}>
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={practitionerName}
            className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-105"
            loading={featured ? 'eager' : 'lazy'}
            fetchPriority={featured ? 'high' : 'auto'}
            decoding="async"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-purple-900 to-[#12061f] p-8">
            <img src={images.wisdomMark} alt="" className="w-20 opacity-70" decoding="async" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#08030f] via-[#08030f]/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
        <p className="mb-1 text-[8px] font-semibold uppercase tracking-[0.12em] text-yellow-200/80 sm:text-[10px] sm:tracking-[0.2em]">
            {practitioner.Archetype || 'Featured practitioner'}
          </p>
          <h3 className="font-title text-sm leading-tight text-white sm:text-2xl">
            {practitionerName}
          </h3>
          <p className="mt-1 line-clamp-2 text-[9px] leading-4 text-white/70 sm:text-xs sm:leading-5">
            {formatProfileText(practitioner.Title)}
          </p>
        </div>
        <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/20 text-yellow-200 opacity-0 backdrop-blur-sm transition group-hover:opacity-100">
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
};

const HeroGuides = ({ featuredPractitioners }) => (
  <div className="relative">
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-200/70">
          Top Guides
        </p>
        <p className="mt-2 max-w-xs text-sm leading-6 text-white/60">
          Meet the guides ready to support your next chapter.
        </p>
      </div>
      <Link
        to="/practitioners"
        className="hidden items-center gap-1 text-xs font-semibold uppercase tracking-[0.16em] text-yellow-200 transition hover:text-white sm:flex"
      >
        View All <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>

    {featuredPractitioners.length === 3 ? (
      <div className="grid grid-cols-3 gap-2 pb-6 sm:gap-4 sm:pb-0">
        {featuredPractitioners.map((practitioner, index) => (
          <div key={practitioner.id || practitioner.ID} className="min-w-0">
            <HeroPractitionerCard practitioner={practitioner} featured={index === 1} />
          </div>
        ))}
      </div>
    ) : (
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-64 animate-pulse rounded-2xl bg-white/10 sm:h-[25rem]" />
        ))}
      </div>
    )}

    <Link
      to="/practitioners"
      className="mt-2 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-yellow-200 transition hover:text-white sm:hidden"
    >
      View All <ArrowUpRight className="h-4 w-4" />
    </Link>
  </div>
);

const Hero = () => {
  const [featuredPractitioners, setFeaturedPractitioners] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetchPractitioners().then((practitioners) => {
      if (!mounted || !practitioners.length) return;
      setFeaturedPractitioners(getRandomPractitioners(practitioners));
    });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#09050f]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_46%,rgba(93,36,160,0.6),transparent_34%),radial-gradient(circle_at_15%_30%,rgba(250,190,35,0.1),transparent_25%),linear-gradient(120deg,#09050f_0%,#12061f_55%,#050307_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(115deg,transparent_0%,rgba(255,255,255,0.08)_48%,transparent_49%)] opacity-30" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-20 pt-32 sm:px-6 lg:px-8 lg:pb-24">
        <div className="grid items-center gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <div className="text-center lg:text-left">
            <p className="mb-5 text-xs font-semibold uppercase tracking-[0.45em] text-yellow-200/80">
              The Wisdom Network
            </p>
            <h1 className="mb-6 font-title text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              <span className="block whitespace-nowrap bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                REAL SHIFTS.
              </span>
              <span className="block whitespace-nowrap bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                REAL RESULTS.
              </span>
            </h1>

            <div className="mt-10 lg:hidden">
              <HeroGuides featuredPractitioners={featuredPractitioners} />
            </div>

            <p className="mb-10 max-w-lg text-base leading-7 text-white/75 sm:text-lg">
              Book private 1 on 1 time with a highly advanced practitioner who meets you exactly where you are and helps you move all the way through it. Come for a single session or a full 1 on 1 program. We also host live and always-available Experiences and themed Memberships if you&apos;re looking for a group setting.
            </p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap lg:justify-start">
              <Button
                onClick={() => window.open('https://twnhub.online/services', '_blank', 'noopener,noreferrer')}
                className="rounded-lg bg-gradient-to-r from-yellow-500 to-yellow-400 px-6 py-3.5 text-sm font-bold text-black shadow-xl transition-all duration-300 hover:from-yellow-400 hover:to-yellow-300 hover:shadow-2xl"
              >
                BOOK YOUR 1 ON 1 SESSION
              </Button>
              <Link
                to="/experiences"
                className="rounded-lg border border-yellow-300/40 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-yellow-100 transition-colors hover:border-yellow-200 hover:bg-yellow-200/10"
              >
                Explore Experiences
              </Link>
              <Link
                to="/membership"
                className="rounded-lg border border-white/20 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white/80 transition-colors hover:border-white/50 hover:bg-white/10 hover:text-white"
              >
                See Memberships
              </Link>
            </div>
          </div>

          <div className="hidden lg:block">
            <HeroGuides featuredPractitioners={featuredPractitioners} />
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="animate-bounce">
          <div className="flex h-10 w-6 justify-center rounded-full border-2 border-yellow-400">
            <div className="mt-2 h-3 w-1 animate-pulse rounded-full bg-yellow-400" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;